/**
 * DashboardLayout.js - Layout Principal Responsivo
 * CG Business ERP - Suporte para Desktop e Mobile
 */

import { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar, { MobileHeader } from './Sidebar';
import { GlobalBirthdayBanner } from './GlobalBirthdayBanner';
import { Toaster } from '@/components/ui/sonner';
import WelcomeTour from '@/components/WelcomeTour';

export default function DashboardLayout() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const persistCollapsedState = useCallback((nextState) => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(nextState));
  }, []);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load collapsed state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved !== null) {
      try {
        setIsCollapsed(JSON.parse(saved));
      } catch {
        setIsCollapsed(false);
      }
    }
  }, []);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((previousState) => {
      const nextState = !previousState;
      persistCollapsedState(nextState);
      return nextState;
    });
  }, [persistCollapsedState]);

  const handleToggleMobile = useCallback(() => {
    setIsMobileOpen((prev) => !prev);
  }, []);

  const handleCloseMobile = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, isMobileOpen]);

  return (
    <div className="flex h-screen w-full max-w-full overflow-hidden bg-slate-50">
      {/* Mobile Header */}
      {isMobile && <MobileHeader onMenuClick={handleToggleMobile} showUtilities={false} />}

      {/* Sidebar */}
      <Sidebar 
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        isMobile={isMobile}
        isMobileOpen={isMobileOpen}
        onCloseMobile={handleCloseMobile}
      />

      {/* Main Content */}
      <main
        className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-slate-50"
        style={isMobile ? { paddingTop: 'calc(56px + env(safe-area-inset-top, 0px))' } : undefined}
      >
        <GlobalBirthdayBanner />
        <Outlet />
      </main>

      {/* Toast Notifications */}
      <Toaster position="top-right" />

      {/* Welcome Tour */}
      <WelcomeTour />
    </div>
  );
}
